'use client';

import React, { useState } from 'react';
import { NodeBio } from '@/utils/drupal-api/__generated__/graphql';

import { ImageSrcSetSize } from '@/utils/image';

import DrupalImage from '@/components/ui/drupal-image';
import ModalDialog from '@/components/ui/modal-dialog';
import InfoIcon from '@/components/icon/info-icon';

// Bio images in the CMS seem to have been uploaded only at 340px width
const bioImageSrcsetSizes: ImageSrcSetSize[] = [{ viewport: 0, image: 340 }];

interface BioCardProps {
  bio: NodeBio;
  className?: string;
}

export const BioCard: React.FC<BioCardProps> = ({ bio, className }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={className}>
      {bio.bioImage && bio.body ? (
        <div onClick={() => setIsOpen(true)} className="cursor-pointer">
          <DrupalImage
            image={bio.bioImage}
            srcsetSizes={bioImageSrcsetSizes}
            className="aspect-[13/15] w-full rounded-xl object-cover object-top"
          />
        </div>
      ) : bio.bioImage ? (
        <DrupalImage
          image={bio.bioImage}
          srcsetSizes={bioImageSrcsetSizes}
          className="aspect-[13/15] w-full rounded-xl object-cover object-top"
        />
      ) : null}
      <div className="px-3 py-2">
        {bio.title && bio.body ? (
          <div className="flex items-baseline justify-between gap-4">
            <h3 className="ut-md mb-1.5 text-black-2">{bio.title}</h3>
            <div className="group relative">
              <span
                className="cursor-pointer text-gray-7 transition-colors hover:text-link"
                onClick={() => setIsOpen(true)}
              >
                <InfoIcon className="h-4 w-4" />
              </span>
              <span className="absolute -right-[6px] -top-[44px] origin-bottom scale-0 transform rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-all group-hover:scale-100 group-hover:opacity-100">
                View Bio
              </span>
            </div>
          </div>
        ) : bio.title ? (
          <h3 className="ut-md mb-1.5 text-black-2">{bio.title}</h3>
        ) : null}
        <div className="space-y-1.5">
          {bio.jobTitle && <p className="ut-sm text-gray-7">{bio.jobTitle}</p>}
          {bio.phone && (
            <p className="sm text-gray-7">
              <a
                href={`tel:${bio.phone}`}
                className="hover:text-link hover:no-underline"
              >
                {bio.phone}
              </a>
            </p>
          )}
          {bio.email && (
            <p className="sm text-gray-7">
              <a
                href={`mailto:${bio.email}`}
                className="hover:text-link hover:no-underline"
              >
                {bio.email}
              </a>
            </p>
          )}
          {bio.license && <p className="sm text-gray-7">{bio.license}</p>}
        </div>
      </div>
      <ModalDialog id={bio.uuid} open={isOpen} onclose={() => setIsOpen(false)}>
        <div className="flex flex-col p-5">
          {bio.title && (
            <h3 className="ut-md mb-1.5 text-black-2">{bio.title}</h3>
          )}
          {bio.jobTitle && (
            <p className="ut-sm mb-4 text-gray-7">{bio.jobTitle}</p>
          )}
          {bio.body && (
            <div
              className="ut-sm text-gray-7"
              dangerouslySetInnerHTML={{ __html: bio.body.processed }}
            />
          )}
        </div>
      </ModalDialog>
    </div>
  );
};
